import type { Ring, Vec2 } from '../../../../geo/types';
import type { Frame, MeshBuilder } from '../../../mesh/meshBuilder';
import { prismSolid, ringPoints } from '../../solids';
import {
  corbelBand,
  finial,
  grown,
  profileRoof,
  ringBand,
  ringCentre,
  roofTrim,
  towerRoof,
  type FortressStyles,
} from './fortressParts';
import { panel } from './fortressBlocks';
import { dormer, pinnacles, radialCorbels, vane } from './towerDetails';
import { waagStone } from './sintAntoniespoortStone';
import {
  BIG_TOWER,
  FOOT,
  HOOFDPOORT,
  METSELAARSTOREN,
  SINT_LUCASTOREN,
  VANE,
  VOORPOORT_TOWER,
  rectRing,
  type RoundTower,
} from './sintAntoniespoortPlan';

/* Windows stand this far proud of the round and reach this far into it, so the dark shows on every
   face of the polygon the round is drawn as. */
const PANEL = { proud: 0.05, depth: 0.2 } as const;
const FINIAL_RADIUS = { big: 0.1, small: 0.07 } as const;
/* Finials start this far down inside the roof they stand on, so no gap opens at its apex. */
const FINIAL_SEAT = 0.3;

interface Openings {
  readonly sill: number;
  readonly head: number;
  readonly width: number;
  readonly at: readonly number[];
}

function openings(
  builder: MeshBuilder,
  style: FortressStyles['dark'],
  frame: Frame,
  tower: {
    readonly centre: Vec2;
    readonly radius: number;
    readonly facing: number;
    readonly hand: number;
  },
  spec: Openings & { readonly arch: boolean },
  proud: number = PANEL.proud
): void {
  for (const at of spec.at) {
    const facing = tower.facing + tower.hand * at;
    const on: Vec2 = [
      tower.centre[0] + tower.radius * Math.cos(facing),
      tower.centre[1] + tower.radius * Math.sin(facing),
    ];
    panel(builder, style, frame, { ...spec, at: on, facing, depth: PANEL.depth, proud });
  }
}

/* A spire's finial and the vane on its rod, turned to the tower's outer side. */
function spireTop(
  builder: MeshBuilder,
  roof: FortressStyles['roof'],
  frame: Frame,
  at: {
    readonly centre: Vec2;
    readonly apex: number;
    readonly top: number;
    readonly facing: number;
  },
  radius: number
): void {
  finial(builder, roof, frame, at.centre, { base: at.apex - FINIAL_SEAT, top: at.top }, radius);
  vane(
    builder,
    roofTrim(roof),
    frame,
    { centre: at.centre, top: at.top, azimuth: at.facing },
    VANE
  );
}

/* A round body's lower part, banded, carries its upper part on the same ring. */
const OPEN = { top: false, bottom: false } as const;

/* Hood moulds stand this far proud, behind the window's dark. */
const SURROUND_PROUD = 0.03;

/* The dormers' backs sink this far toward the axis, well into the roof. */
const DORMER_BACK = 0.9;

function roofRadiusAt(tower: RoundTower, y: number): number {
  const { roofProfile, apex } = BIG_TOWER;
  const rings = [...roofProfile, [apex, 0] as const];
  const k = rings.findIndex(([h]) => h >= y);
  const [y0, s0] = rings[Math.max(0, k - 1)]!;
  const [y1, s1] = rings[k]!;
  return tower.radius * (s0 + ((s1 - s0) * (y - y0)) / (y1 - y0));
}

function bigTowerRoof(
  builder: MeshBuilder,
  styles: FortressStyles,
  frame: Frame,
  tower: RoundTower
): void {
  const { segments, apex, finialTop, roofProfile } = BIG_TOWER;
  const { window, ...d } = BIG_TOWER.dormer;
  const profile = roofProfile.map(([y, share]) => [y, tower.radius * share] as const);
  profileRoof(builder, styles.roof, frame, { centre: tower.centre, segments, profile, apex });
  for (let k = 0; k < 4; k++) {
    const r: [number, number] = [DORMER_BACK, roofRadiusAt(tower, d.sill) + d.out];
    dormer(builder, styles, frame, tower.centre, {
      ...d,
      azimuth: tower.facing + (k * Math.PI) / 2,
      r,
      ...(k === 0 ? { window } : {}),
    });
  }
  const top = { centre: tower.centre, apex, top: finialTop, facing: tower.facing };
  spireTop(builder, styles.roof, frame, top, FINIAL_RADIUS.big);
}

/** E or S: a round body with its windows under a moulded eave, a bell roof with dormers and a vane. */
export function bigTower(
  builder: MeshBuilder,
  styles: FortressStyles,
  frame: Frame,
  tower: RoundTower
): void {
  const { segments, eave, cornice, windows, surround, smallWindows } = BIG_TOWER;
  const ring = ringPoints(tower.centre, tower.radius, segments, 0);
  const bandsTo = HOOFDPOORT.bandsTo;
  prismSolid(builder, waagStone(styles).gobertange, frame, ring, FOOT, bandsTo, OPEN);
  prismSolid(builder, styles.wall, frame, ring, bandsTo, eave, { top: true, bottom: false });
  corbelBand(builder, styles.trim, frame, ring, cornice.reach, [eave - cornice.depth, eave]);
  const moulds = {
    ...windows,
    width: windows.width + 2 * surround,
    sill: windows.sill - surround,
    arch: true,
  };
  openings(builder, styles.trim, frame, tower, moulds, SURROUND_PROUD);
  openings(builder, styles.dark, frame, tower, { ...windows, arch: true });
  openings(builder, styles.dark, frame, tower, { ...smallWindows, arch: false });
  bigTowerRoof(builder, styles, frame, tower);
}

function voorpoortTop(
  builder: MeshBuilder,
  styles: FortressStyles,
  frame: Frame,
  tower: RoundTower
): void {
  const { segments, gallery, parapet, pinnacles: p, apex, finialTop } = VOORPOORT_TOWER;
  const at = (r: number) => ringPoints(tower.centre, r, segments, 0);
  const outer = tower.radius + gallery.reach;
  ringBand(builder, styles.trim, frame, { inner: at(tower.radius), outer: at(outer) }, [
    gallery.from,
    gallery.to,
  ]);
  const walk = at(outer - parapet.thickness);
  ringBand(builder, styles.wall, frame, { inner: walk, outer: at(outer) }, [
    gallery.to,
    parapet.to,
  ]);
  const azimuths = [0, 1, 2, 3].map((k) => tower.facing + Math.PI / 4 + (k * Math.PI) / 2);
  const on = { centre: tower.centre, radius: outer - parapet.thickness / 2, azimuths };
  pinnacles(builder, roofTrim(styles.roof), frame, on, {
    base: parapet.to,
    top: p.top,
    radius: p.radius,
  });
  const spire = ringPoints(tower.centre, tower.radius, 8, Math.PI / 8);
  towerRoof(builder, styles.roof, frame, spire, { eave: gallery.to, apex });
  const top = { centre: tower.centre, apex, top: finialTop, facing: tower.facing };
  spireTop(builder, styles.roof, frame, top, FINIAL_RADIUS.small);
}

/** The octagonal plinth under K or TA, as wide across its faces as the round body plus its offset. */
export function voorpoortPlinth(tower: RoundTower): Ring {
  const across = (tower.radius + VOORPOORT_TOWER.plinth.proud) / Math.cos(Math.PI / 8);
  return ringPoints(tower.centre, across, 8, Math.PI / 8);
}

/** K or TA: a stone plinth, a round body with its slits, a corbelled gallery with pinnacles, a spire and a vane. */
export function voorpoortTower(
  builder: MeshBuilder,
  styles: FortressStyles,
  frame: Frame,
  tower: RoundTower
): void {
  const { segments, plinth, bandsTo, corbels, gallery, slits } = VOORPOORT_TOWER;
  const round = ringPoints(tower.centre, tower.radius, segments, 0);
  const stone = waagStone(styles);
  prismSolid(builder, stone.sandstone, frame, voorpoortPlinth(tower), FOOT, plinth.top);
  prismSolid(builder, stone.ledesteen, frame, round, plinth.top, bandsTo, OPEN);
  prismSolid(builder, styles.wall, frame, round, bandsTo, gallery.to, {
    top: true,
    bottom: false,
  });
  const corbelSpec = { ...corbels, reach: gallery.reach * 0.8 };
  radialCorbels(builder, styles.trim, frame, tower, corbelSpec, [corbels.from, corbels.to]);
  openings(builder, styles.dark, frame, tower, { ...slits, arch: false });
  voorpoortTop(builder, styles, frame, tower);
}

function squareRing(centre: Vec2, half: number): Ring {
  return rectRing([centre[0] - half, centre[0] + half], [centre[1] - half, centre[1] + half]);
}

/** L, octagonal with a skirted spire and a vane, and M, square and low under a pyramid. */
export function stairTowers(builder: MeshBuilder, styles: FortressStyles, frame: Frame): void {
  const lucas = SINT_LUCASTOREN;
  const capped = { top: true, bottom: false };
  prismSolid(builder, styles.wall, frame, lucas.ring, FOOT, lucas.eave, capped);
  const { depth, reach } = lucas.cornice;
  corbelBand(builder, styles.trim, frame, lucas.ring, reach, [lucas.eave - depth, lucas.eave]);
  const roofRing = grown(lucas.ring, reach);
  towerRoof(
    builder,
    styles.roof,
    frame,
    roofRing,
    { eave: lucas.eave, apex: lucas.apex },
    lucas.kick
  );
  const top = {
    centre: ringCentre(lucas.ring),
    apex: lucas.apex,
    top: lucas.finialTop,
    facing: Math.PI,
  };
  spireTop(builder, styles.roof, frame, top, FINIAL_RADIUS.small);
  const mason = METSELAARSTOREN;
  const square = squareRing(mason.centre, mason.half);
  prismSolid(builder, styles.wall, frame, square, FOOT, mason.eave, capped);
  towerRoof(builder, styles.roof, frame, square, { eave: mason.eave, apex: mason.apex });
}

/** Plan rings of every tower at its foot, for the gate's footprint and obstacles. */
export function towerFeet(big: readonly RoundTower[], small: readonly RoundTower[]): Ring[] {
  return [
    ...big.map((t) => ringPoints(t.centre, t.radius, BIG_TOWER.segments, 0)),
    ...small.map(voorpoortPlinth),
    SINT_LUCASTOREN.ring,
    squareRing(METSELAARSTOREN.centre, METSELAARSTOREN.half),
  ];
}
